/**
 * DeclarationCard.js
 *
 * The look of an Unclaimed Declarations card, shared by the printed wall cards (/fly/admin) and the guests'
 * deck (/fly/immigration), so a guest instantly recognises the card in front of them:
 * a navy airline band, "CUSTOMS DECLARATION" and a big "No. 07".
 */

import React, { useEffect, useState } from "react";
import { setZXingModuleOverrides, writeBarcode } from "zxing-wasm/writer";
import { Plane } from "lucide-react";

export const CARD_INK = "#1B2A41";
export const CARD_PAPER = "#FBF7EF";
export const CARD_RED = "#C8442F";
const MONO = "'JetBrains Mono', monospace";

// The QR writer's WebAssembly comes from this site (public/zxing), not a CDN
setZXingModuleOverrides({
    locateFile: (path, prefix) => (path.endsWith(".wasm") ? "/zxing/zxing_writer-3.1.3.wasm" : prefix + path),
});

/**
 * "07"
 */
export const cardLabel = (number) => String(number).padStart(2, "0");

/**
 * The card's header band
 * @param {Number} number       -   The card's number
 * @param {Boolean} compact     -   Smaller, for short phone screens
 * @param {ReactNode} children  -   Anything else on the band's right (e.g. a pin button)
 */
export const CardBand = ({ number, compact = false, children }) => (
    <div className={`flex items-center justify-between gap-3 ${compact ? "px-4 py-2" : "px-5 py-3"}`} style={{ background: CARD_INK, color: CARD_PAPER }}>
        <div className="flex flex-col min-w-0">
            <span className="flex items-center gap-1.5 text-[10px] font-bold tracking-[0.2em] opacity-80 whitespace-nowrap overflow-hidden">
                <Plane size={12} className="shrink-0" aria-hidden="true" /> <span className="truncate">RIGHT BROTHER AIRWAYS</span>
            </span>
            {/* One line even on a 320px phone */}
            <span className={`font-bold whitespace-nowrap ${compact ? "text-[13px] tracking-[0.1em] min-[360px]:text-sm min-[360px]:tracking-[0.16em]" : "text-base tracking-[0.18em]"}`}>CUSTOMS DECLARATION</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
            {children}
            <span className="flex flex-col items-end leading-none">
                <span className="text-[10px] font-bold tracking-[0.2em] opacity-80">No.</span>
                <span className={`font-bold ${compact ? "text-2xl" : "text-4xl"}`} style={{ fontFamily: MONO }}>{cardLabel(number)}</span>
            </span>
        </div>
    </div>
);

/**
 * A QR code for a link, drawn as SVG
 * @param {String} value    -   e.g. 'https://isayyashtothat.com/fly/immigration?card=07'
 * @param {Number} size     -   Width and height in px
 */
export const QrCode = ({ value, size = 96 }) => {
    const [svg, setSvg] = useState("");
    useEffect(() => {
        let ignore = false;
        writeBarcode(value, { format: "QRCode" })
            // The writer's SVG has a fixed size and no viewBox: give it one, so it scales to the box (sharp edges for scanners)
            .then((result) => {
                if (ignore || result.error) return;
                setSvg(result.svg.replace(/<svg width="(\d+)" height="(\d+)"/, '<svg viewBox="0 0 $1 $2" width="100%" height="100%" shape-rendering="crispEdges"'));
            })
            .catch((err) => console.error("Couldn't draw a QR code:", err));
        return () => { ignore = true; };
    }, [value]);
    return (
        <span
            className="block bg-white"
            style={{ width: size, height: size }}
            role="img"
            aria-label={`QR code for ${value}`}
            // Our own QR writer's output for our own link
            dangerouslySetInnerHTML={{ __html: svg }}
        />
    );
};

/**
 * The printed wall card: band, the three statements, and a QR code to file a report on it
 * @param {Object} card     -   { number, statements, url }
 */
export const PrintedCard = ({ card }) => (
    <div className="rounded-2xl overflow-hidden border-2 flex flex-col" style={{ borderColor: CARD_INK, background: CARD_PAPER, color: CARD_INK, breakInside: "avoid" }}>
        <CardBand number={card.number} />
        <div className="flex gap-4 p-5">
            <ol className="flex-1 flex flex-col gap-3 list-decimal pl-5 text-base leading-snug">
                {card.statements.map((statement, index) => <li key={index}>{statement}</li>)}
            </ol>
            {card.url && (
                <div className="flex flex-col items-center gap-1 shrink-0">
                    <QrCode value={card.url} size={96} />
                    <span className="text-[9px] font-bold tracking-[0.14em] text-center" style={{ color: CARD_RED }}>SCAN TO FILE<br />YOUR REPORT</span>
                </div>
            )}
        </div>
        <p className="px-5 pb-4 text-[10px] font-bold tracking-[0.18em]" style={{ color: "#5B6577" }}>WHO DECLARED THIS? WHICH ONE IS THE LIE?</p>
    </div>
);
